import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from './ThemeContext';
import './App.css';

function BannerUpload() {
  const navigate = useNavigate();
  const { theme, toggleTheme, isDark } = useTheme();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render key

  // Bulk end date state
  const [bulkEndDate, setBulkEndDate] = useState('');

  // Excel upload states
  const [excelFile, setExcelFile] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState('');
  const [excelData, setExcelData] = useState(null);
  const [uploadMode, setUploadMode] = useState('session'); // 'session' or 'excel'

  // Modal state for image preview
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedImageAlt, setSelectedImageAlt] = useState('');

  // Dropdown visibility states
  const [showBannerTypeDropdown, setShowBannerTypeDropdown] = useState(null);
  const [showBannerShowtypeDropdown, setShowBannerShowtypeDropdown] = useState(null);

  // Custom input states for banner_showtype
  const [customShowtype, setCustomShowtype] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  useEffect(() => {
    // Get banner data from sessionStorage (set by main App)
    try {
      const bannerData = sessionStorage.getItem('bannerData');
      if (bannerData) {
        const parsedData = JSON.parse(bannerData);
        setBanners(parsedData);
        setTotalPages(Math.ceil(parsedData.length / 50));
      } else {
        setError('No banner data found. Please go back and click "Banner Upload Table" first.');
      }
    } catch (err) {
      console.error('Error accessing sessionStorage:', err);
      setError('Error loading banner data. Please try again.');
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both dropdowns and their triggers
      const isOutsideBannerType = !event.target.closest('.banner-type-button') && !event.target.closest('.banner-type-dropdown');
      const isOutsideBannerShowtype = !event.target.closest('.banner-showtype-button') && !event.target.closest('.banner-showtype-dropdown') && !event.target.closest('.custom-input-container');

      if (isOutsideBannerType) {
        setShowBannerTypeDropdown(null);
      }
      if (isOutsideBannerShowtype) {
        setShowBannerShowtypeDropdown(null);
        // Also close custom input if open
        if (showCustomInput) {
          setShowCustomInput(false);
          setCustomShowtype('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomInput]);



  const handleToggleActive = (index) => {
    const globalIndex = (currentPage - 1) * 50 + index;
    const updatedBanners = [...banners];
    updatedBanners[globalIndex].active = updatedBanners[globalIndex].active === 1 ? 0 : 1;
    setBanners(updatedBanners);
  };

  const handleToggleAnimated = (index) => {
    const globalIndex = (currentPage - 1) * 50 + index;
    const updatedBanners = [...banners];
    updatedBanners[globalIndex].animated = updatedBanners[globalIndex].animated === 1 ? 0 : 1;
    setBanners(updatedBanners);
  };

  const handleEndDateChange = (index, value) => {
    const globalIndex = (currentPage - 1) * 50 + index;
    const updatedBanners = [...banners];
    updatedBanners[globalIndex].enddate = value;
    setBanners(updatedBanners);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!allowedTypes.includes(file.type)) {
        setExcelError('Please select a valid Excel file (.xlsx or .xls)');
        setExcelFile(null);
        return;
      }
      setExcelFile(file);
      setExcelError('');
      setExcelData(null); // Clear previous data
    }
  };

  const handleTextChange = (index, value) => {
    const globalIndex = (currentPage - 1) * 50 + index;
    const updatedBanners = [...banners];
    updatedBanners[globalIndex].text = value;
    setBanners(updatedBanners);
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      setExcelError('Please select an Excel file first');
      return;
    }

    // Clear previous messages
    setSuccessMessage('');
    setError('');

    setExcelLoading(true);
    setExcelError('');

    try {
      console.log('📊 EXCEL BANNER UPLOAD: Starting Excel file upload');

      const formData = new FormData();
      formData.append('excel_file', excelFile);

      const response = await axios.post('/api/banners/upload-excel/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 second timeout for file upload
      });

      console.log('📊 EXCEL BANNER UPLOAD: API Response:', response.data);

      if (response.data.success) {
        console.log('✅ EXCEL BANNER UPLOAD: Successfully parsed', response.data.valid_banners, 'banners');

        // Set the parsed data
        setExcelData(response.data.data);
        setBanners(response.data.data);
        setUploadMode('excel');
        setTotalPages(Math.ceil(response.data.data.length / 50));
        setCurrentPage(1);

        // Show success message
        showMessage(`✅ Successfully parsed ${response.data.valid_banners} banners from Excel file`, 'success');

        // Show validation errors if any
        if (response.data.validation_errors && response.data.validation_errors.length > 0) {
          const errorMsg = `⚠️ ${response.data.validation_errors.length} validation errors found. Check console for details.`;
          console.warn('📊 EXCEL BANNER UPLOAD: Validation errors:', response.data.validation_errors);
          showMessage(errorMsg, 'error');
        }

      } else {
        console.error('❌ EXCEL BANNER UPLOAD: Upload failed:', response.data.error);
        setExcelError(response.data.error);
      }

    } catch (err) {
      console.error('❌ EXCEL BANNER UPLOAD: Error during Excel upload:', err);

      let errorMessage = 'Failed to upload Excel file';

      if (err.response) {
        console.error('❌ EXCEL BANNER UPLOAD: Response status:', err.response.status);
        console.error('❌ EXCEL BANNER UPLOAD: Response data:', err.response.data);

        if (err.response.data && err.response.data.error) {
          errorMessage = `Server error: ${err.response.data.error}`;
        } else {
          errorMessage = `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        console.error('❌ EXCEL BANNER UPLOAD: No response received:', err.request);
        errorMessage = 'No response from server. Please check if the backend is running.';
      } else {
        console.error('❌ EXCEL BANNER UPLOAD: Request setup error:', err.message);
        errorMessage = 'Error setting up request: ' + err.message;
      }

      setExcelError(errorMessage);
    }

    setExcelLoading(false);
  };

  const switchToSessionData = () => {
    try {
      const bannerData = sessionStorage.getItem('bannerData');
      if (bannerData) {
        const parsedData = JSON.parse(bannerData);
        setBanners(parsedData);
        setUploadMode('session');
        setTotalPages(Math.ceil(parsedData.length / 50));
        setCurrentPage(1);
        setExcelData(null);
        setExcelFile(null);
        setExcelError('');
        showMessage('Switched to session banner data', 'success');
      } else {
        setError('No session banner data found');
      }
    } catch (err) {
      console.error('Error loading session data:', err);
      setError('Error loading session banner data');
    }
  };

  const removeBanner = (index) => {
    const globalIndex = (currentPage - 1) * 50 + index;
    const updatedBanners = [...banners];
    updatedBanners.splice(globalIndex, 1);
    setBanners(updatedBanners);
    setTotalPages(Math.ceil(updatedBanners.length / 50));
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  };

  // Bulk end date handler
  const handleBulkEndDateChange = () => {
    if (!bulkEndDate) {
      return; // Silent fail if no date selected
    }

    console.log('🎯 BULK: Setting all end dates to:', bulkEndDate);

    // Validate that bulk date is not before any start date
    const earliestStartDate = banners
      .map(banner => banner.startdate)
      .filter(date => date)
      .sort()
      [0];

    if (earliestStartDate && bulkEndDate < earliestStartDate) {
      return; // Silent fail if date is invalid
    }

    // Update all banners with the bulk end date
    setBanners(prevBanners => {
      const updated = prevBanners.map(banner => ({
        ...banner,
        enddate: bulkEndDate
      }));
      console.log('✅ BULK: Updated all', updated.length, 'banners with end date:', bulkEndDate);
      return updated;
    });

    setRefreshKey(prev => prev + 1); // Force re-render
  };

  // Banner type and showtype change handlers - FORCE RE-RENDER
  const handleBannerTypeChange = (index, newType) => {
    const globalIndex = (currentPage - 1) * 50 + index;

    // Force re-render approach
    if (globalIndex >= 0 && globalIndex < banners.length) {
      const updatedBanners = [...banners];
      const oldType = updatedBanners[globalIndex].banner_type;

      updatedBanners[globalIndex] = {
        ...updatedBanners[globalIndex],
        banner_type: newType,
        animated: newType === 'animation' ? 1 : updatedBanners[globalIndex].animated
      };

      setBanners(updatedBanners);
      setRefreshKey(prev => prev + 1); // Force re-render
    } else {
      console.error('❌ FORCE Index out of bounds');
    }

    setShowBannerTypeDropdown(null);
  };

  const handleBannerShowtypeChange = (index, newShowtype) => {
    const globalIndex = (currentPage - 1) * 50 + index;

    // Force re-render approach
    if (globalIndex >= 0 && globalIndex < banners.length) {
      const updatedBanners = [...banners];
      const oldShowtype = updatedBanners[globalIndex].banner_showtype;

      if (newShowtype === 'custom') {
        // Show custom input field instead of updating banner
        setShowCustomInput(true);
        setCustomShowtype('');
        setShowBannerShowtypeDropdown(null);
        return;
      }

      updatedBanners[globalIndex] = {
        ...updatedBanners[globalIndex],
        banner_showtype: newShowtype
      };

      setBanners(updatedBanners);
      setRefreshKey(prev => prev + 1); // Force re-render
    } else {
      console.error('❌ FORCE Index out of bounds');
    }

    setShowBannerShowtypeDropdown(null);
  };

  // Handler for custom showtype input submission
  const handleCustomShowtypeSubmit = (index) => {
    if (!customShowtype.trim()) {
      return; // Don't submit empty values
    }

    const globalIndex = (currentPage - 1) * 50 + index;

    if (globalIndex >= 0 && globalIndex < banners.length) {
      const updatedBanners = [...banners];
      updatedBanners[globalIndex] = {
        ...updatedBanners[globalIndex],
        banner_showtype: customShowtype.trim()
      };

      setBanners(updatedBanners);
      setRefreshKey(prev => prev + 1); // Force re-render
    }

    // Reset custom input state
    setShowCustomInput(false);
    setCustomShowtype('');
  };

  // Available options
  const bannerTypeOptions = ['side', 'main', 'middle', 'animation', 'not_categorized'];
  const bannerShowtypeOptions = ['reel', 'frontpage', 'custom'];

  // Image preview modal handlers
  const handleImageClick = (imageUrl, imageAlt) => {
    setSelectedImage(imageUrl);
    setSelectedImageAlt(imageAlt);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage('');
    setSelectedImageAlt('');
  };

  // Function to display messages
  const showMessage = (message, type = 'success') => {
    // Clear existing error state
    setError('');
    setErrorDetails([]);

    // Set the message
    if (type === 'success') {
      setSuccessMessage(message);
    } else {
      setError(message);
    }
  };

  const handleUpload = async () => {
    // Clear previous messages
    setSuccessMessage('');
    setError('');
    setErrorDetails([]);

    setLoading(true);

    try {
      console.log('🎯 DATABASE: Starting database upload for', banners.length, 'banners');

      // Prepare data for database upload (exclude aspect_ratio and original_dimension)
      const uploadData = {
        banners: banners.map(banner => ({
          banner_type: banner.banner_type,
          image: banner.image,
          active: banner.active,
          animated: banner.animated,
          url: banner.url,
          created_at: banner.created_at,
          update_at: banner.update_at,
          text: banner.text,
          banner_showtype: banner.banner_showtype,
          startdate: banner.startdate,
          enddate: banner.enddate
        }))
      };

      console.log('📊 DATABASE: Sending', uploadData.banners.length, 'banners to database API');

      // Send to database API
      const response = await axios.post('/api/banners/upload/', uploadData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 60000, // 60 second timeout for large uploads
      });

      console.log('📊 DATABASE: API Response:', response.data);

      if (response.data.success) {
        console.log('✅ DATABASE: Successfully uploaded', response.data.uploaded_count, 'banners to database');

        // Show success message
        const successMsg = `✅ Successfully uploaded ${response.data.uploaded_count} banners to database at ${response.data.timestamp}`;
        showMessage(successMsg, 'success');

        // Optional: Clear the banners data after successful upload
        // setBanners([]);
        // setTotalPages(0);

      } else {
        console.error('❌ DATABASE: Upload failed:', response.data.error);

        // Check if detailed error information is available
        if (response.data.details && Array.isArray(response.data.details) && response.data.details.length > 0) {
          console.error('❌ DATABASE: Detailed errors:', response.data.details);

          // Set main error message and detailed error list separately
          const errorMessage = response.data.error || 'Upload failed';
          setError(errorMessage);
          setErrorDetails(response.data.details);
        } else {
          // Fallback to simple error message
          const errorMsg = response.data.error || 'Upload failed';
          setError(errorMsg);
          setErrorDetails([]);
        }
      }

    } catch (err) {
      console.error('❌ DATABASE: Error during database upload:', err);

      let errorMessage = 'Failed to upload banners to database';
      let details = [];

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
        if (err.response.data.details) {
          details = err.response.data.details;
        }
      }

      setError(errorMessage);
      setErrorDetails(details);
    }

    setLoading(false);
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * 50;
    const endIndex = startIndex + 50;
    return banners.slice(startIndex, endIndex);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <button
          key={number}
          className={`mx-1 px-3 py-1 rounded ${
            number === currentPage
              ? 'bg-perzsi-yellow text-perzsi-gray font-semibold'
              : 'bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50'
          }`}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center mt-6">
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          First
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {items}
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          Last
        </button>
      </div>
    );
  };

  if (error && !banners.length) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
          <button
            className="perzsi-button-secondary mt-4"
            onClick={() => navigate('/')}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className="perzsi-gradient shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <img
                  src="/perzsi-icon-black.png"
                  alt="Perzsi.com"
                  className="h-12 w-auto"
                />
                <p className="text-yellow-100">Banner Upload Table</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-white text-right">
                <p className="text-sm opacity-90">Transformed Banner Data</p>
              </div>
              <button
                onClick={toggleTheme}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors duration-200"
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full px-4 py-8">
        {/* Control Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
              Banner Upload {uploadMode === 'excel' ? '(Excel Data)' : '(Session Data)'}
            </h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Review and modify the {uploadMode === 'excel' ? 'parsed Excel' : 'transformed'} banner data before uploading.
            </p>
            <button
              className="perzsi-button-primary w-full"
              onClick={handleUpload}
              disabled={loading || banners.length === 0}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-perzsi-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                `Upload ${banners.length} Banners`
              )}
            </button>

            {/* Success/Error Message Display */}
            {successMessage && (
              <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg animate-fade-in">
                <div className="flex items-center">
                  <span className="text-lg mr-2">✅</span>
                  <span className="text-sm font-medium">{successMessage}</span>
                </div>
              </div>
            )}
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Upload Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Data Source:</span>
                <span className="font-semibold capitalize">{uploadMode}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Banners:</span>
                <span className="font-semibold">{banners.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Active:</span>
                <span className="font-semibold text-green-600">
                  {banners.filter(b => b.active === 1).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Animated:</span>
                <span className="font-semibold text-blue-600">
                  {banners.filter(b => b.animated === 1).length}
                </span>
              </div>
              {uploadMode === 'excel' && excelData && (
                <div className="flex justify-between">
                  <span>Parsed from Excel:</span>
                  <span className="font-semibold text-green-600">✓</span>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t">
              <button
                className="perzsi-button-secondary w-full"
                onClick={() => navigate('/ads')}
              >
                ← Back to Ads Table
              </button>
            </div>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Bulk End Date</h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Set the same end date for all banners at once.
            </p>
            <div className="space-y-3">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select End Date:
                </label>
                <input
                  type="date"
                  value={bulkEndDate}
                  onChange={(e) => setBulkEndDate(e.target.value)}
                  className="perzsi-input w-full"
                  min={new Date().toISOString().split('T')[0]} // Minimum date is today
                />
              </div>
              <button
                className="perzsi-button-primary w-full"
                onClick={handleBulkEndDateChange}
                disabled={!bulkEndDate || banners.length === 0}
              >
                Set All End Dates ({banners.length})
              </button>
            </div>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Excel File Upload</h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Upload banner data from Excel file. Expected columns: <strong>image</strong>, <strong>url</strong>, <strong>text</strong>, <strong>banner_type</strong>, <strong>banner_showtype</strong>, <strong>active</strong>, <strong>animated</strong>, <strong>startdate</strong> (required), enddate (optional).
              Supports .xlsx and .xls formats.
            </p>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Excel File:
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-perzsi-yellow file:text-perzsi-gray hover:file:bg-yellow-400"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  className="perzsi-button-primary flex-1"
                  onClick={handleExcelUpload}
                  disabled={excelLoading || !excelFile}
                >
                  {excelLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-perzsi-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Parsing...
                    </span>
                  ) : (
                    'Parse Excel File'
                  )}
                </button>

                {uploadMode === 'excel' && (
                  <button
                    className="perzsi-button-secondary"
                    onClick={switchToSessionData}
                  >
                    Switch to Session Data
                  </button>
                )}
              </div>

              {excelError && (
                <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">❌</span>
                    <span className="text-sm font-medium">{excelError}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <div className="font-semibold mb-2">
              ❌ {error}
            </div>
            {errorDetails && errorDetails.length > 0 && (
              <div className="mt-3 pt-3 border-t border-red-300">
                <div className="text-sm font-medium mb-2 text-red-800">
                  Error Details ({errorDetails.length} issues):
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {errorDetails.map((detail, index) => (
                    <div
                      key={index}
                      className="text-sm bg-red-50 px-2 py-1 rounded border-l-2 border-red-400"
                    >
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Banner Upload Table */}
        {banners.length > 0 && (
          <div className="perzsi-table-container">
            <div className="overflow-x-auto" style={{ overflow: 'visible' }}>
              <table className="perzsi-table">
                <thead>
                  <tr>
                    <th>banner_type</th>
                    <th>image</th>
                    <th>active</th>
                    <th>animated</th>
                    <th>url</th>
                    <th>created_at</th>
                    <th>update_at</th>
                    <th>text</th>
                    <th>banner_showtype</th>
                    <th>startdate</th>
                    <th>enddate</th>
                    <th>aspect_ratio</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentPageData().map((banner, index) => {
                    const globalIndex = (currentPage - 1) * 50 + index;

                    return (
                      <tr key={index} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td>
                          <div className="relative">
                            <button
                              className={`banner-type-button px-2 py-1 rounded text-xs font-semibold cursor-pointer hover:opacity-80 ${
                                banner.banner_type === 'not_categorized'
                                  ? 'bg-gray-500 text-white'
                                  : banner.banner_type === 'side'
                                  ? 'bg-blue-500 text-white'
                                  : banner.banner_type === 'main'
                                  ? 'bg-green-500 text-white'
                                  : banner.banner_type === 'middle'
                                  ? 'bg-yellow-500 text-white'
                                  : 'bg-purple-500 text-white'
                              }`}
                              onClick={() => setShowBannerTypeDropdown(showBannerTypeDropdown === index ? null : index)}
                              title="Click to change banner type"
                            >
                              {banner.banner_type} ▼
                            </button>
                            {showBannerTypeDropdown === index && (
                              <div
                                className="banner-type-dropdown fixed bg-white border border-gray-300 rounded-md shadow-xl"
                                style={{
                                  zIndex: 9999,
                                  minWidth: '128px',
                                  position: 'fixed'
                                }}
                                ref={(el) => {
                                  if (el && showBannerTypeDropdown === index) {
                                    const rect = el.previousSibling?.getBoundingClientRect();
                                    if (rect) {
                                      const dropdownHeight = bannerTypeOptions.length * 32 + 16; // Estimate height
                                      const windowHeight = window.innerHeight;
                                      const bottomSpace = windowHeight - rect.bottom;

                                      if (bottomSpace < dropdownHeight && rect.top > dropdownHeight) {
                                        // Show above if not enough space below
                                        el.style.top = `${rect.top - dropdownHeight - 8}px`;
                                        el.style.left = `${rect.left}px`;
                                      } else {
                                        // Show below
                                        el.style.top = `${rect.bottom + 4}px`;
                                        el.style.left = `${rect.left}px`;
                                      }
                                    }
                                  }
                                }}
                              >
                                {bannerTypeOptions.map((type) => (
                                  <div
                                    key={type}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleBannerTypeChange(index, type);
                                    }}
                                    onClick={(e) => {
                                      // Prevent default click behavior
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    className={`px-3 py-2 text-xs cursor-pointer hover:bg-gray-100 select-none ${
                                      banner.banner_type === type ? 'bg-yellow-50 text-perzsi-gray font-semibold' : 'text-gray-700'
                                    }`}
                                    style={{ userSelect: 'none' }}
                                  >
                                    {type}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="ads-image-container">
                            <img
                              src={banner.image}
                              alt={`Banner ${index + 1}`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                              onClick={() => handleImageClick(banner.image, `Banner ${index + 1}`)}
                              style={{
                                maxWidth: '100px',
                                maxHeight: '50px',
                                marginRight: '5px',
                                cursor: 'pointer',
                                border: '2px solid transparent',
                                borderRadius: '4px'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.border = '2px solid #FFD700';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.border = '2px solid transparent';
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <button
                            className={`w-8 h-8 rounded flex items-center justify-center text-white font-bold ${
                              banner.active === 1 ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'
                            }`}
                            onClick={() => handleToggleActive(index)}
                            title={banner.active === 1 ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                          >
                            {banner.active === 1 ? '✓' : '✗'}
                          </button>
                        </td>
                        <td>
                          <button
                            className={`w-8 h-8 rounded flex items-center justify-center text-white font-bold ${
                              banner.animated === 1 ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-500 hover:bg-gray-600'
                            }`}
                            onClick={() => handleToggleAnimated(index)}
                            title={banner.animated === 1 ? 'Animated - Click to disable' : 'Static - Click to enable'}
                          >
                            {banner.animated === 1 ? '✓' : '✗'}
                          </button>
                        </td>
                        <td className="max-w-xs truncate">
                          <a
                            href={banner.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            Link
                          </a>
                        </td>
                        <td>{banner.created_at}</td>
                        <td>{banner.update_at}</td>
                        <td>
                          <input
                            type="text"
                            value={banner.text || ''}
                            onChange={(e) => handleTextChange(index, e.target.value)}
                            className={`w-full border rounded px-2 py-1 text-sm ${
                              isDark
                                ? 'bg-gray-700 border-gray-600 text-white focus:bg-gray-600 focus:border-perzsi-yellow'
                                : 'border-gray-300 bg-white text-gray-900 focus:border-blue-500'
                            }`}
                          />
                        </td>
                        <td>
                          <div className="relative">
                            <button
                              className={`banner-showtype-button px-2 py-1 rounded text-xs font-semibold cursor-pointer hover:opacity-80 ${
                                banner.banner_showtype === 'frontpage'
                                  ? 'bg-indigo-500 text-white'
                                  : banner.banner_showtype === 'reel'
                                  ? 'bg-pink-500 text-white'
                                  : 'bg-gray-500 text-white'
                              }`}
                              onClick={() => setShowBannerShowtypeDropdown(showBannerShowtypeDropdown === index ? null : index)}
                              title="Click to change banner showtype"
                            >
                              {banner.banner_showtype} ▼
                            </button>
                            {showBannerShowtypeDropdown === index && (
                              <div
                                className="banner-showtype-dropdown fixed bg-white border border-gray-300 rounded-md shadow-xl"
                                style={{
                                  zIndex: 9999,
                                  minWidth: '120px',
                                  position: 'fixed'
                                }}
                                ref={(el) => {
                                  if (el && showBannerShowtypeDropdown === index) {
                                    const rect = el.previousSibling?.getBoundingClientRect();
                                    if (rect) {
                                      const dropdownHeight = bannerShowtypeOptions.length * 32 + 16; // Estimate height
                                      const windowHeight = window.innerHeight;
                                      const bottomSpace = windowHeight - rect.bottom;

                                      if (bottomSpace < dropdownHeight && rect.top > dropdownHeight) {
                                        // Show above if not enough space below
                                        el.style.top = `${rect.top - dropdownHeight - 8}px`;
                                        el.style.left = `${rect.left}px`;
                                      } else {
                                        // Show below
                                        el.style.top = `${rect.bottom + 4}px`;
                                        el.style.left = `${rect.left}px`;
                                      }
                                    }
                                  }
                                }}
                              >
                                {bannerShowtypeOptions.map((showtype) => (
                                  <div
                                    key={showtype}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleBannerShowtypeChange(index, showtype);
                                    }}
                                    onClick={(e) => {
                                      // Prevent default click behavior
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    className={`px-3 py-2 text-xs cursor-pointer hover:bg-gray-100 select-none ${
                                      banner.banner_showtype === showtype ? 'bg-yellow-50 text-perzsi-gray font-semibold' : 'text-gray-700'
                                    }`}
                                    style={{ userSelect: 'none' }}
                                  >
                                    {showtype === 'custom' ? 'Custom...' : showtype}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Custom input field - shows when showCustomInput is true for this row */}
                            {showCustomInput && showBannerShowtypeDropdown === index && (
                              <div
                                className="custom-input-container fixed bg-white border border-gray-300 rounded-md shadow-xl p-2"
                                style={{
                                  zIndex: 10000,
                                  minWidth: '200px',
                                  position: 'fixed'
                                }}
                                ref={(el) => {
                                  if (el && showBannerShowtypeDropdown === index) {
                                    const rect = el.previousSibling?.previousSibling?.getBoundingClientRect();
                                    if (rect) {
                                      const inputHeight = 60; // Estimate height for input field
                                      const windowHeight = window.innerHeight;
                                      const bottomSpace = windowHeight - rect.bottom;

                                      if (bottomSpace < inputHeight && rect.top > inputHeight) {
                                        // Show above if not enough space below
                                        el.style.top = `${rect.top - inputHeight - 8}px`;
                                        el.style.left = `${rect.left}px`;
                                      } else {
                                        // Show below
                                        el.style.top = `${rect.bottom + 4}px`;
                                        el.style.left = `${rect.left}px`;
                                      }
                                    }
                                  }
                                }}
                              >
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={customShowtype}
                                    onChange={(e) => setCustomShowtype(e.target.value)}
                                    placeholder="Enter custom showtype..."
                                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-perzsi-yellow"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleCustomShowtypeSubmit(index);
                                      } else if (e.key === 'Escape') {
                                        e.preventDefault();
                                        setShowCustomInput(false);
                                        setCustomShowtype('');
                                      }
                                    }}
                                    autoFocus
                                  />
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleCustomShowtypeSubmit(index);
                                    }}
                                    className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                    title="Save custom value"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setShowCustomInput(false);
                                      setCustomShowtype('');
                                    }}
                                    className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                    title="Cancel"
                                  >
                                    ✗
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{banner.startdate}</td>
                        <td>
                          <input
                            type="date"
                            value={banner.enddate}
                            onChange={(e) => handleEndDateChange(index, e.target.value)}
                            className={`border rounded px-2 py-1 text-sm ${
                              isDark
                                ? 'bg-gray-700 border-gray-600 text-white focus:bg-gray-600 focus:border-perzsi-yellow'
                                : 'border-gray-300 bg-white text-gray-900 focus:border-blue-500'
                            }`}
                            min={banner.startdate}
                          />
                        </td>
                        <td className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {(() => {
                            const dimension = banner.original_dimension && banner.original_dimension !== 'N/A'
                              ? banner.original_dimension
                              : null;

                            if (dimension) {
                              // Clean dimension to remove any extra ratio data that might be appended
                              const cleanDimension = dimension.split(' :: ')[0]; // Remove anything after ' :: '
                              return `${cleanDimension} :: ${banner.aspect_ratio}`;
                            } else {
                              return `${banner.aspect_ratio} (No dimensions)`;
                            }
                          })()}
                        </td>
                        <td>
                          <button
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors duration-200"
                            onClick={() => removeBanner(index)}
                            title="Remove this banner"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {renderPagination()}

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            className="perzsi-button-secondary"
            onClick={() => navigate('/ads')}
          >
            ← Back to Ads Table
          </button>
        </div>
      </div>

      {/* Image Preview Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-perzsi-gray">Image Preview</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="text-center">
                {selectedImage && (
                  <img
                    src={selectedImage}
                    alt={selectedImageAlt}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  className="perzsi-button-primary"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BannerUpload;
